<template>
  <div class="bg-secondary-dark h-full">
    <div>
      <div class="flex justify-end pr-2 pt-2">
        <button
          type="button"
          class="text-secondary-lighter hover:text-common-white hover:bg-secondary rounded-sm p-1 cursor-pointer transition-colors"
          :aria-label="sidebarCollapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'"
          @click="handleSidebarToggleClick"
        >
          <BIconChevronDoubleRight v-if="sidebarCollapsed" class="w-5 h-5" />
          <BIconChevronDoubleLeft v-else class="w-5 h-5" />
        </button>
      </div>

      <div class="flex items-center px-4 pt-3 pb-6 w-auto overflow-hidden">
        <div class="flex gap-4 overflow-hidden">
          <p
            v-if="!sidebarCollapsed"
            class="text-red font-bold my-auto truncate"
          >
            Admin Dashboard
          </p>
        </div>
      </div>
      <div class="flex grow flex-col">
        <nav class="flex-1 space-y-2 px-2 justify-center" aria-label="Sidebar">
          <NuxtLink
            v-for="n in nav"
            :key="n.to"
            :to="n.to"
            class="group flex justify-between py-2 px-2 text-md"
          >
            <component
              :is="n.icon"
              :class="[
                n.isActive
                  ? 'text-lilac'
                  : 'text-secondary-lighter group-hover:text-common-white',
                sidebarCollapsed ? 'my-auto' : 'mr-3',
                'w-6 h-6',
              ]"
            />

            <span
              v-if="!sidebarCollapsed"
              :class="[
                n.isActive
                  ? 'text-lilac font-bold'
                  : 'text-secondary-lighter group-hover:text-common-white',
                'flex-1 ',
              ]"
            >{{ n.title }}</span>

            <span
              v-if="n.alerts > 0 && !sidebarCollapsed"
              :class="[
                n.isActive ? 'bg-white' : 'bg-gray-100 group-hover:bg-gray-200',
                'ml-3 inline-block py-0.5 px-3 rounded-full',
              ]"
            >{{ n?.alerts }}</span>
          </NuxtLink>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup lang='ts'>
import {
  BIconPerson,
  BIconJustify,
  BIconGear,
  BIconChevronDoubleLeft,
  BIconChevronDoubleRight, BIconCrosshair, BIconExclamationOctagon,
  BIconMicrosoft,
  BIconHddRackFill,
  BIconCreditCard,
  BIconExclamationTriangle,
  BIconDiagram3,
  BIconEye
} from 'bootstrap-icons-vue'
import { useCurrentUserSession } from '~/composables/states'

const route = useRoute()
const { currentEnv } = useCurrentUserSession()

// Start expanded on every screen size so the submenus stay reachable; collapsing
// is a manual choice via the toggle above.
const sidebarCollapsed = ref(false)

const nav = computed(() => {
  const navs = [
    {
      title: 'Dienstleister',
      to: '/admin/serviceprovider',
      icon: BIconPerson,
      isActive: route.path.includes('admin/serviceprovider'),
      alerts: 0
    },
    {
      title: 'Reseller',
      to: '/admin/reseller',
      icon: BIconDiagram3,
      isActive: route.path.includes('admin/reseller'),
      alerts: 0
    },
    {
      title: 'Globale Angriffe',
      to: '/admin/attacks',
      icon: BIconCrosshair,
      isActive: route.path.includes('admin/attacks'),
      alerts: 0
    },
    {
      title: 'Globale Findings',
      to: '/admin/findings',
      icon: BIconExclamationOctagon,
      isActive: route.path.includes('admin/findings'),
      alerts: 0
    },
    {
      title: 'Preview Findings',
      to: '/admin/ioe-preview-findings',
      icon: BIconEye,
      isActive: route.path.includes('admin/ioe-preview-findings'),
      alerts: 0
    },
    {
      title: 'Entra Sync-Fehler',
      to: '/admin/entra-sync-failures',
      icon: BIconExclamationTriangle,
      isActive: route.path.includes('admin/entra-sync-failures'),
      alerts: 0
    },
    {
      title: 'Einstellungen',
      to: '/admin/settings',
      icon: BIconGear,
      isActive: route.path.includes('admin/settings'),
      alerts: 0
    }
  ]

  if (currentEnv.isProdEnv || currentEnv.isNextEnv) {
    navs.splice(6, 0, {
      title: 'MS Graph',
      to: '/admin/graph-explorer',
      icon: BIconMicrosoft,
      isActive: route.path.includes('admin/graph-explorer'),
      alerts: 0
    })
    navs.splice(7, 0, {
      title: 'Selfhosted',
      to: '/admin/selfhosted',
      icon: BIconHddRackFill,
      isActive: route.path.includes('admin/selfhosted'),
      alerts: 0
    })
    navs.splice(8, 0, {
      title: 'Billing',
      to: '/admin/billing',
      icon: BIconCreditCard,
      isActive: route.path.includes('admin/billing'),
      alerts: 0
    })
  }

  return navs
})

function handleSidebarToggleClick (): void {
  sidebarCollapsed.value = !sidebarCollapsed.value
}
</script>

<style scoped></style>
